import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Send, Eye, Pencil, Trash2, FlaskConical, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";

const opts = { withCredentials: true };
const EMPTY = { subject: "", title: "", body: "", image_url: "", button_label: "", button_url: "" };
const inputCls = "w-full bg-[#FAF9F6] border border-[#D1CFC7] focus:border-[#2C402E] px-3 py-2 outline-none font-body text-sm";
const STATUS = { draft: "Brouillon", sending: "Envoi en cours…", sent: "Envoyée", failed: "Envoi interrompu — vous pouvez relancer" };
const errMsg = (e, fallback) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

const F = ({ k, label, form, setForm, area, placeholder }) => (
  <div className={area ? "md:col-span-2" : ""}>
    <label className="text-xs font-body text-[#4A4A4A] mb-1 block">{label}</label>
    {area ? (
      <textarea rows={8} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`campaign-${k}-input`} className={inputCls} placeholder={placeholder} />
    ) : (
      <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`campaign-${k}-input`} className={inputCls} placeholder={placeholder} />
    )}
  </div>
);

export function CampaignsPanel() {
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const load = () => {
    api.get("/admin/campaigns", opts).then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/admin/campaigns/config", opts).then((r) => setConfig(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!items.some((c) => c.status === "sending")) return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [items]);

  const save = async () => {
    if (!form.subject?.trim() || !form.title?.trim() || !form.body?.trim()) return toast.error("Objet, titre et texte sont obligatoires");
    const payload = Object.fromEntries(Object.entries(EMPTY).map(([k]) => [k, form[k] === "" ? null : form[k]]));
    try {
      if (form.id) await api.put(`/admin/campaigns/${form.id}`, payload, opts);
      else await api.post("/admin/campaigns", payload, opts);
      toast.success("Campagne enregistrée");
      setForm(null); setPreview(null); load();
    } catch (e) { toast.error(errMsg(e, "Erreur d'enregistrement")); }
  };
  const showPreview = async (c) => {
    try {
      const r = await api.get(`/admin/campaigns/${c.id}/preview`, { ...opts, responseType: "text" });
      setPreview({ id: c.id, html: r.data });
    } catch { toast.error("Aperçu indisponible"); }
  };
  const sendTest = async (c) => {
    if (!testEmail) return toast.error("Indiquez une adresse pour l'e-mail de test");
    try { await api.post(`/admin/campaigns/${c.id}/test`, { email: testEmail }, opts); toast.success(`E-mail de test envoyé à ${testEmail}`); }
    catch (e) { toast.error(errMsg(e, "Envoi de test impossible")); }
  };
  const sendAll = async (c) => {
    if (!window.confirm(`Envoyer « ${c.subject} » à ${config?.subscribers ?? 0} inscrit(s) ? Cette action est définitive.`)) return;
    try { await api.post(`/admin/campaigns/${c.id}/send`, {}, opts); toast.success("Envoi lancé"); load(); }
    catch (e) { toast.error(errMsg(e, "Envoi impossible")); }
  };
  const remove = async (c) => { if (!window.confirm("Supprimer cette campagne ?")) return; await api.delete(`/admin/campaigns/${c.id}`, opts); load(); };

  return (
    <div data-testid="campaigns-panel">
      {config?.test_mode && (
        <div data-testid="campaigns-test-mode-warning" className="flex gap-3 border border-[#9E4735] bg-[#9E4735]/5 p-4 mb-6 text-sm font-body text-[#1A1A1A]">
          <AlertTriangle size={18} className="text-[#9E4735] flex-shrink-0 mt-0.5" />
          <p>Mode test Resend (expéditeur <code>{config.sender}</code>) : les e-mails ne partent que vers l'adresse de votre compte Resend. Dès que le domaine challengeslitteraireseditions.com est « Verified », l'envoi à tous les inscrits sera activé.</p>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="font-body text-sm text-[#4A4A4A]">{config?.subscribers ?? 0} inscrit(s) recevront la prochaine campagne.</p>
        <div className="flex gap-2">
          <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="E-mail de test" data-testid="campaign-test-email-input" className={`${inputCls} w-56`} />
          <button onClick={() => setForm({ ...EMPTY })} data-testid="new-campaign-button" className="bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body flex items-center gap-2 whitespace-nowrap"><Plus size={14} /> Nouvelle campagne</button>
        </div>
      </div>
      {form && (
        <div data-testid="campaign-form" className="border border-[#2C402E] bg-[#F4F1EA] p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <F k="subject" label="Objet de l'e-mail *" form={form} setForm={setForm} />
          <F k="title" label="Titre affiché *" form={form} setForm={setForm} />
          <F k="body" label="Texte * (ligne vide = nouveau paragraphe)" form={form} setForm={setForm} area />
          <F k="image_url" label="URL de l'image (optionnel)" form={form} setForm={setForm} placeholder="https://challengeslitteraireseditions.com/covers/…" />
          <div />
          <F k="button_label" label="Texte du bouton (optionnel)" form={form} setForm={setForm} placeholder="Lire l'histoire" />
          <F k="button_url" label="Lien du bouton" form={form} setForm={setForm} placeholder="https://challengeslitteraireseditions.com/histoires/…" />
          <div className="md:col-span-2 flex gap-2">
            <button onClick={save} data-testid="save-campaign" className="bg-[#2C402E] text-[#FAF9F6] px-6 py-2 text-sm font-body">Enregistrer</button>
            <button onClick={() => setForm(null)} data-testid="cancel-campaign" className="border border-[#D1CFC7] px-6 py-2 text-sm font-body">Annuler</button>
          </div>
        </div>
      )}
      <ul className="space-y-3">
        {items.map((c) => (
          <li key={c.id} data-testid={`campaign-${c.id}`} className="border border-[#D1CFC7] p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg text-[#1A1A1A] truncate">{c.subject}</p>
                <p className="text-xs font-body text-[#757575]" data-testid={`campaign-status-${c.id}`}>
                  {STATUS[c.status]}{c.status !== "draft" ? ` · ${c.sent_count}/${c.recipients} envoyé(s)${c.failed_count ? ` · ${c.failed_count} échec(s)` : ""}` : ""}
                  {c.sent_at ? ` · ${new Date(c.sent_at).toLocaleString("fr-FR")}` : ""}
                </p>
              </div>
              <button onClick={() => (preview?.id === c.id ? setPreview(null) : showPreview(c))} data-testid={`preview-campaign-${c.id}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><Eye size={12} /> Aperçu</button>
              <button onClick={() => sendTest(c)} data-testid={`test-campaign-${c.id}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><FlaskConical size={12} /> E-mail de test</button>
              {(c.status === "draft" || c.status === "failed") && <>
                <button onClick={() => setForm({ ...EMPTY, ...c })} data-testid={`edit-campaign-${c.id}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><Pencil size={12} /> Éditer</button>
                <button onClick={() => sendAll(c)} data-testid={`send-campaign-${c.id}`} className="text-xs font-body bg-[#9E4735] text-[#FAF9F6] px-3 py-1.5 flex items-center gap-1"><Send size={12} /> Envoyer à tous</button>
              </>}
              <button onClick={() => remove(c)} data-testid={`delete-campaign-${c.id}`} className="text-xs font-body text-[#9E4735]"><Trash2 size={12} /></button>
            </div>
            {preview?.id === c.id && <iframe title="Aperçu" srcDoc={preview.html} data-testid="campaign-preview-frame" className="w-full h-[640px] mt-4 border border-[#E8E4D9] bg-white" />}
          </li>
        ))}
      </ul>
    </div>
  );
}
